import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from '../../components/ui/sonner';
import axios from 'axios';
import {
  Clock,
  LogIn,
  LogOut,
  Calendar,
  Timer,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Time Display Component
function TimeDisplay() {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="text-center">
      <p className="text-6xl font-light text-white font-['Outfit'] tabular-nums">
        {time.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
      </p>
      <p className="text-zinc-500 mt-2">
        {time.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
      </p>
    </div>
  );
}

// Fichaje Row
function FichajeRow({ fichaje }) {
  const formatTime = (dateStr) => {
    if (!dateStr) return '--:--';
    return new Date(dateStr).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const calcDuration = () => {
    if (!fichaje.entrada || !fichaje.salida) return '--';
    const entrada = new Date(fichaje.entrada);
    const salida = new Date(fichaje.salida);
    const diff = (salida - entrada) / (1000 * 60); // minutes
    const hours = Math.floor(diff / 60);
    const minutes = Math.round(diff % 60);
    return `${hours}h ${minutes}m`;
  };

  const isComplete = fichaje.entrada && fichaje.salida;

  return (
    <div className={`flex items-center p-4 rounded-xl border ${isComplete ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-amber-500/[0.05] border-amber-500/20'}`}>
      <div className="flex-1 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-sky-500/10 flex items-center justify-center">
          <Calendar className="w-5 h-5 text-sky-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-white">
            {new Date(fichaje.fecha).toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short' })}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-8">
        <div className="text-center">
          <p className="text-xs text-zinc-500">Entrada</p>
          <p className="text-sm text-white font-medium">{formatTime(fichaje.entrada)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-zinc-500">Salida</p>
          <p className="text-sm text-white font-medium">{formatTime(fichaje.salida)}</p>
        </div>
        <div className="text-center min-w-[60px]">
          <p className="text-xs text-zinc-500">Total</p>
          <p className={`text-sm font-medium ${isComplete ? 'text-emerald-400' : 'text-amber-400'}`}>
            {calcDuration()}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FichajePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  const [todayStatus, setTodayStatus] = useState(null); // null, 'entrada', 'completo'
  const [fichajes, setFichajes] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const fetchFichajes = async () => {
    setLoading(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const response = await axios.get(`${API_URL}/api/rrhh/fichajes?year=${year}&month=${month}`, { withCredentials: true });
      setFichajes(response.data.fichajes || []);
      
      // Check today's status
      const today = new Date().toISOString().split('T')[0];
      const todayFichaje = (response.data.fichajes || []).find(f => f.fecha === today);
      
      if (!todayFichaje) {
        setTodayStatus(null);
      } else if (todayFichaje.entrada && !todayFichaje.salida) {
        setTodayStatus('entrada');
      } else {
        setTodayStatus('completo');
      }
    } catch (err) {
      console.error('Error fetching fichajes:', err);
      setFichajes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFichajes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handlePunch = async (tipo) => {
    setPunching(true);
    try {
      await axios.post(`${API_URL}/api/rrhh/fichajes`, { tipo }, { withCredentials: true });
      toast.success(tipo === 'entrada' ? 'Entrada registrada' : 'Salida registrada');
      fetchFichajes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al fichar');
    } finally {
      setPunching(false);
    }
  };

  const prevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    if (next <= new Date()) {
      setCurrentMonth(next);
    }
  };

  const totalHoras = fichajes.reduce((acc, f) => {
    if (!f.entrada || !f.salida) return acc;
    const diff = (new Date(f.salida) - new Date(f.entrada)) / (1000 * 60 * 60);
    return acc + diff;
  }, 0);

  return (
    <ComercialLayout
      title="Fichaje"
      subtitle="Control de jornada laboral"
      onLogout={handleLogout}
    >
      {/* Punch Clock Section */}
      <div className="p-8 rounded-2xl bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] mb-8">
        <TimeDisplay />

        <div className="flex items-center justify-center gap-4 mt-8">
          <Button
            onClick={() => handlePunch('entrada')}
            disabled={punching || todayStatus === 'entrada' || todayStatus === 'completo'}
            className={`min-w-[140px] h-14 text-lg ${
              todayStatus === null 
                ? 'bg-emerald-500 hover:bg-emerald-600' 
                : 'bg-zinc-700 cursor-not-allowed'
            } text-white`}
            data-testid="fichar-entrada-btn"
          >
            {punching ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : todayStatus === 'entrada' || todayStatus === 'completo' ? (
              <><CheckCircle className="w-5 h-5 mr-2" />Entrada</>
            ) : (
              <><LogIn className="w-5 h-5 mr-2" />Entrada</>
            )}
          </Button>

          <Button
            onClick={() => handlePunch('salida')}
            disabled={punching || todayStatus !== 'entrada'}
            className={`min-w-[140px] h-14 text-lg ${
              todayStatus === 'entrada' 
                ? 'bg-red-500 hover:bg-red-600' 
                : 'bg-zinc-700 cursor-not-allowed'
            } text-white`}
            data-testid="fichar-salida-btn"
          >
            {punching ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : todayStatus === 'completo' ? (
              <><CheckCircle className="w-5 h-5 mr-2" />Salida</>
            ) : (
              <><LogOut className="w-5 h-5 mr-2" />Salida</>
            )}
          </Button>
        </div>

        {/* Status indicator */}
        <div className="text-center mt-6">
          {todayStatus === null && (
            <p className="text-zinc-500">No has fichado hoy</p>
          )}
          {todayStatus === 'entrada' && (
            <p className="text-emerald-400 flex items-center justify-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Jornada en curso
            </p>
          )}
          {todayStatus === 'completo' && (
            <p className="text-sky-400">Jornada completada</p>
          )}
        </div>
      </div>

      {/* Monthly Summary */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={prevMonth}
            className="text-zinc-400 hover:text-white"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h3 className="text-lg font-light text-white font-['Outfit'] capitalize">
            {currentMonth.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={nextMonth}
            disabled={new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1) > new Date()}
            className="text-zinc-400 hover:text-white disabled:opacity-30"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.02] border border-white/[0.06]">
          <Timer className="w-4 h-4 text-sky-400" />
          <span className="text-sm text-zinc-400">Total:</span>
          <span className="text-white font-medium">{totalHoras.toFixed(1)}h</span>
        </div>
      </div>

      {/* Fichajes List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-sky-400 animate-spin" />
        </div>
      ) : fichajes.length === 0 ? (
        <div className="text-center py-12">
          <Clock className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">No hay fichajes este mes</p>
        </div>
      ) : (
        <div className="space-y-3">
          {fichajes.map((fichaje, index) => (
            <FichajeRow key={fichaje.id || index} fichaje={fichaje} />
          ))}
        </div>
      )}

      {/* Info Odoo */}
      <div className="mt-8 p-4 rounded-xl bg-amber-500/[0.08] border border-amber-500/20">
        <p className="text-sm text-amber-300/80 text-center font-['Manrope']">
          Preparado para integración con Odoo RRHH. Los fichajes se sincronizarán automáticamente.
        </p>
      </div>
    </ComercialLayout>
  );
}
